// SignUp.js
import React, { useState } from "react";
import axiosInstance from "../utils/axiosInstance";

import backendURL, { google_login_api, linkedin_client_id, linkedin_redirect_uri, SETUP_COMPLETE_LOCAL_KEY  } from "../config";
import { GoogleOAuthProvider, GoogleLogin } from '@react-oauth/google';
import { LinkedIn } from 'react-linkedin-login-oauth2';
import { jwtDecode } from "jwt-decode";
import WebsitePopup from './WebsitePopup';
import './SignIn.css'; // Import your custom CSS for styling

// Make sure you have added the custom CSS to your project as suggested.

export default function UserSignUp() {
  
  // --- NO CHANGES TO LOGIC OR STATE ---
  const [formData, setFormData] = useState({ email: "", password: "", webname: "" });
  const [error, setError] = useState(null);
  const [showPopup, setShowPopup] = useState(false);
  const [socialLoginData, setSocialLoginData] = useState(null);
  const [isFormSectionVisible, setIsFormSectionVisible] = useState(true);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    try {
      await axiosInstance.post(`/signup`, formData);
      setError("Your account has been created successfully! We've sent a verification email to you. Please click the link in that email to activate your account. Check your spam/junk folder if you don't see it within a few minutes.");
      setIsFormSectionVisible(false);
      setFormData({ email: "", password: "", webname: "" });
    } catch (error) {
      if (error.response) {
        setError(error.response.data.message || "Signup failed. Please try again.");
      } else if (error.request) {
        setError("No response received from server. Please check your connection.");
      } else {
        setError("An error occurred. Please try again.");
      }
      setIsFormSectionVisible(true);
    }
  };

  const handleGoogleLoginSuccess = async (response) => {
    setError(null);
    setIsFormSectionVisible(true);
    try {
      const { credential } = response;
      const decodedToken = jwtDecode(credential);
      const { sub, email, given_name, family_name, picture } = decodedToken;
      setSocialLoginData({ provider: 'google', data: { email, social_id: sub, given_name, family_name, picture } });
      setShowPopup(true);
    } catch (error) {
      setError("Google signup failed!");
    }
  };

  const handleGoogleLoginFailure = (error) => {
    setError("Google signup failed.");
    setIsFormSectionVisible(true);
  };

  const handleLinkedInLoginSuccess = async (response) => {
    setError(null);
    const authCode = response;
    if (!authCode) {
      setError("LinkedIn authentication code not received.");
      return;
    }
    let payloadObject = { auth_code: authCode, redirect_uri: linkedin_redirect_uri }
    try {
      const tokenResponse = await axiosInstance.post(`/api/linkedin/oauth`, payloadObject, { headers: { 'Content-Type': 'application/json' } });
      const profileResponseData = typeof tokenResponse.data.data === 'string' ? JSON.parse(tokenResponse.data.data) : tokenResponse.data.data;
      const linkedInUser = profileResponseData?.user;
      setSocialLoginData({ provider: 'linkedin', data: { email: linkedInUser.email, social_id: linkedInUser.sub, given_name: linkedInUser.given_name, family_name: linkedInUser.family_name, picture: linkedInUser.picture, } });
      setShowPopup(true);
      setIsFormSectionVisible(true);
    } catch (tokenError) {
      setError("Failed to get access token from LinkedIn. Check console.");
    }
  };

  const handleLinkedInLoginFailure = (error) => {
    if (error?.error != "user_closed_popup") {
      setError("LinkedIn signup failed.");
    }
    setIsFormSectionVisible(true);
  };

  const handlePopupSubmit = async (webname) => {
    if (!socialLoginData) return;
    setError(null);
    let payloadObject = { webname: webname, provider: socialLoginData.provider, email: socialLoginData.data.email, social_id: socialLoginData.data.social_id };
    try {
      const response = await axiosInstance.post(`/social-signup`, payloadObject, { headers: { 'Content-Type': 'application/json' } });
      const responseData = typeof response.data.data === 'string' ? JSON.parse(response.data.data) : response.data.data;
      localStorage.setItem('user', JSON.stringify(responseData.user));
      localStorage.setItem('customer_portal_token', responseData.customer_portal_token);
      if (!responseData.user.profile || responseData.user.active_team.has_pending_terms  || !responseData.user.active_team.subscription) {
          localStorage.removeItem(SETUP_COMPLETE_LOCAL_KEY);
          window.location.href = "/SignUp-Process-Steps";
      }else{
          window.location.href = "/chatbots";
      }
      setShowPopup(false);
      setSocialLoginData(null);
    } catch (error) {
      if (error.response) {
        setError(error.response.data.message || "Social signup failed.");
      } else {
        setError("An error occurred during social signup.");
      }
    }
  };
  // --- END OF UNCHANGED LOGIC ---

  return (
    <GoogleOAuthProvider clientId={google_login_api}>
      <div className={`container-fluid p-0 ${showPopup ? 'd-none' : ''}`}>
        <div className="row g-0 vh-100">
          {/* Left Branding Panel */}
          <div className="col-lg-6 d-none d-lg-block auth-branding-panel h-100">
            <div className="p-5">
              <h1>KlonIT</h1>
              <p className="lead">Join us and start creating today.</p>
            </div>
          </div>

          {/* Right Form Panel */}
          <div className="col-lg-6 auth-form-panel">
            <div className="auth-form-container">

              <div className="text-center mb-4">
                <h1 className="h2 fw-bold">Create Your Account</h1>
                <p className="lead">It's free to get started and explore.</p>
              </div>

              {error && (
                <div className={`alert ${isFormSectionVisible ? 'alert-danger' : 'alert-success'}`}>
                  {error}
                </div>
              )}

              {isFormSectionVisible ? (
                <>
                  <div className="d-grid gap-3 mb-4">
                     <div id="google-login-button">
                        <GoogleLogin
                            onSuccess={handleGoogleLoginSuccess}
                            onError={handleGoogleLoginFailure}
                            text="signup_with"
                            theme="outline"
                            size="large"
                            shape="rectangular"
                            width="100%"
                        />
                     </div>
                    <LinkedIn
                      clientId={linkedin_client_id}
                      redirectUri={linkedin_redirect_uri}
                      onSuccess={handleLinkedInLoginSuccess}
                      onError={handleLinkedInLoginFailure}
                      scope="openid profile email"
                    >
                      {({ linkedInLogin }) => (
                        <button type="button" onClick={linkedInLogin} className="btn btn-linkedin-custom btn-lg">
                           Sign up with LinkedIn
                        </button>
                      )}
                    </LinkedIn>
                  </div>

                  <div className="row align-items-center my-4">
                    <div className="col"><hr /></div>
                    <div className="col-auto text-uppercase divider-text">Or</div>
                    <div className="col"><hr /></div>
                  </div>

                  <form onSubmit={handleSubmit}>
                    <div className="mb-3">
                      <label className="form-label">Email <span className="text-danger">*</span></label>
                      <input className="form-control form-control-lg" type="email" name="email" value={formData.email} onChange={handleChange} placeholder="Enter your email" required />
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Password <span className="text-danger">*</span></label>
                      <input className="form-control form-control-lg" type="password" name="password" value={formData.password} onChange={handleChange} placeholder="Create a password" required />
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Website (Optional)</label>
                      <input className="form-control form-control-lg" type="text" name="webname" value={formData.webname} onChange={handleChange} placeholder="e.g., your-company.com" />
                    </div>
                    <div className="d-grid gap-2 mt-4">
                      <button type="submit" className="btn btn-lg btn-primary">Sign Up</button>
                    </div>
                  </form>

                  <p className="mt-4 small text-center text-muted">
                    By creating an account, you agree to KlonIT's <a href="https://klonit.ai/terms-of-service/" target="_blank" rel="noopener noreferrer">Terms</a> and <a href="https://klonit.ai/privacy-policy-2/" target="_blank" rel="noopener noreferrer">Privacy Policy</a>.
                  </p>
                  <div className="text-center mt-3">
                    Already have an account? <a href="/Login">Log In</a>
                  </div>
                </>
              ) : (
                <div className="text-center">
                    <a href="/Login" className="btn btn-primary">Proceed to Login</a>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* The popup remains unchanged and will overlay everything when active */}
      {showPopup && (
        <WebsitePopup
          onSubmit={handlePopupSubmit}
          onClose={() => {
            setShowPopup(false);
            setSocialLoginData(null);
            setError(null);
            setIsFormSectionVisible(true);
          }}
        />
      )}
    </GoogleOAuthProvider>
  );
}